<script setup lang="ts">
/**
 * HeroSky — the landing hero's night scene: the paper theatre's sky (the same look as
 * the draw's HangingStage), full-bleed and melting into the page top and bottom, a
 * paper moon and stars lowered on twine into the top band, and two paper hills at its
 * foot (the near one cut from the page colour). Decorative only (aria-hidden).
 *
 * Prerendered (vite-ssg), so everything here is static and deterministic — no random,
 * no time, no measuring; the stars' positions are seeded constants and the hills are
 * pre-cut paths (createPaperCutter, seed 7310). The only client-side bit: the CSS loops
 * pause while the hero is scrolled off screen. Static under reduced motion.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

// Pinprick stars: a fixed scatter (golden-ratio-ish steps), in % of the sky.
const STARS = Array.from({ length: 28 }, (_, i) => ({
  x: Math.round(((i * 37.7) % 100) * 10) / 10,
  y: Math.round((3 + ((i * 53.3) % 64)) * 10) / 10,
  s: i % 4 === 0 ? 2 : 1.4,
  tw: i % 3 === 0,
  d: Math.round(((i * 0.41) % 3) * 100) / 100,
  // On phones the copy fills the sky's width below the top band, and a pinprick there
  // reads as punctuation («Мурчим.») — so there only the top-band stars show.
  desk: i > 0,
}));

// Phone-only pinpricks in the top band, above the copy and left of the moon.
const PHONE_STARS = [
  { x: 9, y: 2.2, s: 1.4, tw: true, d: 0.4 },
  { x: 21, y: 5.4, s: 2, tw: false, d: 0 },
  { x: 33, y: 1.6, s: 1.4, tw: false, d: 0 },
  { x: 45, y: 4.6, s: 1.4, tw: true, d: 1.6 },
  { x: 56, y: 2.4, s: 2, tw: false, d: 0 },
  { x: 62, y: 6.2, s: 1.4, tw: true, d: 2.3 },
];

// Props on strings — positions per breakpoint (`m` phone, `d` from md up) sit in the
// gaps beside the copy, so nothing hangs in front of the text. Drop = string length.
const PROPS = [
  { kind: 'moon', xm: 84, xd: 45, dm: 38, dd: 84, sm: 54, sd: 64, dur: 7.2, hideM: false },
  { kind: 'star', xm: 70, xd: 37.5, dm: 14, dd: 34, sm: 16, sd: 20, dur: 5.4, hideM: false },
  { kind: 'star', xm: 96, xd: 53.5, dm: 62, dd: 30, sm: 13, sd: 17, dur: 6.1, hideM: false },
  { kind: 'star', xm: 0, xd: 96, dm: 0, dd: 60, sm: 0, sd: 18, dur: 5.8, hideM: true },
] as const;

// Same cut shapes as the draw stage (HangingStage): the crescent hangs from the top
// of its back (tie point 50,8), a star from its top point (50,2).
const MOON_PATH = 'M 88.4 30.6 A 44 44 0 1 0 55.9 95.6 A 37 37 0 1 1 88.4 30.6 Z';
const STAR_POINTS = '50,2 61,36 97,36 68,57 79,92 50,71 21,92 32,57 3,36 39,36';

const HILL_FAR =
  'M-10 38.3L0 36.6L10 35.7L20 33.8L30 33.6L40 32.9L50 31.9L60 30.1L70 27.7L80 27.6L90 29.7L100 31.2L110 33.9L120 34.9L130 35.5L140 38.2L150 41.2L160 43.6L170 45.3L180 44.6L190 43.2L200 43.5L210 44.3L220 44.3L230 43.7L240 42.5L250 41.6L260 42.1L270 43.7L280 46.4L290 48.5L300 48.8L310 49.1L320 49L330 51.3L340 52.3L350 52.2L360 49.1L370 46.1L380 43.9L390 43L400 41.6L410 40.2L420 36.8L430 35.3L440 34L450 35.8L460 37.8L470 37.9L480 37.8L490 36.2L500 36.8L510 37.3L520 38.2L530 36.4L540 33.9L550 31.5L560 29.5L570 29L580 29.8L590 29.7L600 28.8L610 28.4L620 29.7L630 33L640 36.9L650 38.7L660 39.6L670 40.7L680 41.2L690 43.3L700 44.7L710 45.2L720 43.4L730 41.8L740 41.2L750 41.2L760 43.4L770 43.8L780 44.2L790 43.7L800 45.3L810 48.1L820 50.8L820 80L-10 80Z';
const HILL_NEAR =
  'M-10 65.5L-1 64.3L8 64.1L17 62.4L26 62.7L35 61.4L44 62.2L53 61.7L62 62.6L71 62.7L80 62.7L89 63L98 62.1L107 61.7L116 60.8L125 59.1L134 57.2L143 56.6L152 55.4L161 53.6L170 53.7L179 53.1L188 54.1L197 54.7L206 55.5L215 56.1L224 56.4L233 57.9L242 57.3L251 57.9L260 57.5L269 57.4L278 56.6L287 56.3L296 56.6L305 57.5L314 58.2L323 58.9L332 60.2L341 61.4L350 62.5L359 64L368 65.5L377 66.5L386 66.7L395 66L404 66.2L413 65.6L422 64.4L431 63.1L440 62.5L449 62.1L458 62.5L467 62.4L476 62.4L485 62.8L494 63.4L503 64L512 63.9L521 62.8L530 61.6L539 61.2L548 59.2L557 57.8L566 56.5L575 55.7L584 54.7L593 54.1L602 53.4L611 54.1L620 55L629 55L638 55.8L647 57.3L656 56.9L665 57.4L674 57.2L683 56.8L692 56.9L701 56L710 55.7L719 56.5L728 56.9L737 57.8L746 58L755 59.6L764 61.2L773 62.8L782 64.3L791 65L800 65.4L809 66.1L818 65.4L819 80L-10 80Z';

// Off screen: every CSS loop in the sky holds still (client-only, after hydration).
const el = ref<HTMLElement | null>(null);
const offscreen = ref(false);
let io: IntersectionObserver | null = null;
onMounted(() => {
  if (!el.value || typeof IntersectionObserver === 'undefined') return;
  io = new IntersectionObserver(([entry]) => {
    offscreen.value = !entry.isIntersecting;
  });
  io.observe(el.value);
});
onBeforeUnmount(() => io?.disconnect());
</script>

<template>
  <div
    ref="el"
    :class="['hero-sky pointer-events-none absolute inset-0 overflow-hidden', offscreen && 'is-offscreen']"
    aria-hidden="true"
  >
    <span
      v-for="(s, i) in STARS"
      :key="i"
      :class="['hs-star', s.tw && 'is-twinkle', s.desk && 'is-desk']"
      :style="{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.s}px`, height: `${s.s}px`, animationDelay: `${s.d}s` }"
    />
    <span
      v-for="(s, i) in PHONE_STARS"
      :key="`m${i}`"
      :class="['hs-star is-phone', s.tw && 'is-twinkle']"
      :style="{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.s}px`, height: `${s.s}px`, animationDelay: `${s.d}s` }"
    />

    <svg
      class="hs-hills"
      viewBox="0 0 800 80"
      preserveAspectRatio="none"
    >
      <path
        class="hs-hill-far"
        :d="HILL_FAR"
      />
      <path
        class="hs-hill-near"
        :d="HILL_NEAR"
      />
    </svg>

    <!-- Props on strings: the string ends exactly at the shape's tie point (a knot). -->
    <span
      v-for="(p, i) in PROPS"
      :key="`p${i}`"
      :class="['hs-prop', p.hideM && 'is-desk']"
      :style="{
        '--xm': `${p.xm}%`,
        '--xd': `${p.xd}%`,
        '--dm': `${p.dm}px`,
        '--dd': `${p.dd}px`,
        '--sm': `${p.sm}px`,
        '--sd': `${p.sd}px`,
      }"
    >
      <span
        class="hs-sway"
        :style="{ animationDuration: `${p.dur}s`, animationDelay: `${-i * 1.7}s` }"
      >
        <span class="hs-string" />
        <span
          :class="['hs-shape', `is-${p.kind}`]"
          :style="{ animationDuration: `${p.dur * 1.9}s`, animationDelay: `${-i * 2.3}s` }"
        >
          <svg
            viewBox="0 0 100 100"
            class="block h-auto w-full overflow-visible"
          >
            <!-- its shadow: a dark copy, offset — drawn, not a filter -->
            <path
              v-if="p.kind === 'moon'"
              class="hs-shadow"
              :d="MOON_PATH"
            />
            <polygon
              v-else
              class="hs-shadow"
              :points="STAR_POINTS"
            />
            <path
              v-if="p.kind === 'moon'"
              class="hs-moon"
              :d="MOON_PATH"
            />
            <polygon
              v-else
              class="hs-star-shape"
              :points="STAR_POINTS"
            />
            <circle
              class="hs-knot"
              cx="50"
              :cy="p.kind === 'moon' ? 8 : 2"
              :r="p.kind === 'moon' ? 4 : 9"
            />
          </svg>
        </span>
      </span>
    </span>

    <!-- Melts into the page: static gradients of the page colour (no mask-image). -->
    <span class="hs-fade is-top" />
    <span class="hs-fade is-bottom" />
  </div>
</template>

<style scoped>
/* Scene palette (illustration, not UI tokens) — the paper theatre's night sky,
   shared with the draw stage (HangingStage). */
.hero-sky {
  --hs-sky-top: #0e0c24;
  --hs-sky-mid: #1d1842;
  --hs-sky-low: #2e2660;
  --hs-hill-far: #262052;
  background: linear-gradient(
    to bottom,
    var(--background),
    var(--hs-sky-top) 14%,
    var(--hs-sky-mid) 55%,
    var(--hs-sky-low) 92%
  );
}
.hs-fade {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
}
.hs-fade.is-top {
  top: 0;
  height: 48px;
  background: linear-gradient(var(--background), transparent);
}
.hs-fade.is-bottom {
  bottom: 0;
  height: 56px;
  background: linear-gradient(transparent, var(--background));
}
.hs-star {
  position: absolute;
  border-radius: 50%;
  background: #fff4d6;
  opacity: 0.7;
}
.hs-star.is-twinkle {
  animation: hs-twinkle 3.4s ease-in-out infinite;
}
.hs-star.is-desk {
  display: none;
}
.hs-hills {
  position: absolute;
  left: 0;
  right: 0;
  /* Overlap the bottom edge by 2px: subpixel rounding otherwise leaves a hairline of sky. */
  bottom: -2px;
  width: 100%;
  height: 92px;
}
.hs-hill-far {
  fill: var(--hs-hill-far);
}
.hs-hill-near {
  fill: var(--background);
}

/* ---- The moon and stars on strings (as the draw stage's props) ---- */
.hs-prop {
  position: absolute;
  top: 0;
  left: var(--xm);
  width: 0;
  height: var(--dm);
}
.hs-prop.is-desk {
  display: none;
}
.hs-sway {
  position: absolute;
  inset: 0;
  transform-origin: 50% 0;
  animation: hs-sway 6s ease-in-out infinite alternate;
}
/* Twine, as the puppets' strings. */
.hs-string {
  position: absolute;
  left: -0.75px;
  top: 0;
  width: 1.5px;
  height: 100%;
  background: linear-gradient(90deg, #8e7556 0 25%, #f1e3c4 25% 70%, #a88b66 70%);
  opacity: 0.7;
}
.hs-shape {
  position: absolute;
  top: 100%;
  left: 0;
  width: var(--sm);
  transform-origin: 50% 0;
  animation: hs-twist 11s ease-in-out infinite;
}
.hs-shape.is-moon {
  translate: -50% -8%;
}
.hs-shape.is-star {
  translate: -50% -2%;
}
.hs-shadow {
  fill: rgb(4 3 18 / 0.45);
  transform: translate(2px, 5px);
}
.hs-moon {
  fill: #e6c67d;
  stroke: #f6e3ae;
  stroke-width: 1.5;
}
.hs-star-shape {
  fill: #e4b45a;
  stroke: #f5d992;
  stroke-width: 2.5;
  stroke-linejoin: round;
}
.hs-knot {
  fill: #cdb48d;
}

@media (min-width: 768px) {
  .hs-prop {
    left: var(--xd);
    height: var(--dd);
  }
  .hs-prop.is-desk,
  .hs-star.is-desk {
    display: block;
  }
  .hs-star.is-phone {
    display: none;
  }
  .hs-shape {
    width: var(--sd);
  }
  .hs-hills {
    height: 150px;
  }
  .hs-fade.is-bottom {
    height: 72px;
  }
}

.hero-sky.is-offscreen * {
  animation-play-state: paused !important;
}
@keyframes hs-sway {
  from { transform: rotate(-2.2deg); }
  to { transform: rotate(2.2deg); }
}
@keyframes hs-twist {
  0%, 100% { transform: scaleX(1); }
  50% { transform: scaleX(0.62); }
}
@keyframes hs-twinkle {
  0%, 100% { opacity: 0.75; }
  50% { opacity: 0.2; }
}
@media (prefers-reduced-motion: reduce) {
  .hs-star.is-twinkle,
  .hs-sway,
  .hs-shape {
    animation: none;
  }
}
</style>
